import { create } from 'zustand'

import { type CoreItemPhotoPayload } from '@flecto/types'
import { deepClone, fileToBase64, uploadImageByBase64 } from '@flecto/utils'

import { useItemGeneratorAI } from '../../../flecto-modules-saas/src/items/hooks/useItemGeneratorAI'
import { type ItemPhotoForManager } from './PhotoManager'

export type PhotoManagerState = {
  photo: ItemPhotoForManager
  photoAttributes: CoreItemPhotoPayload

  deletePhoto: () => void
  uploadPhoto: (file: File) => void
  generateSinglePhotoWithAI: (title: string) => Promise<void>
  initializeFromItem: (photoObject: ItemPhotoForManager) => void
}

const emptyPhoto: ItemPhotoForManager = {
  type: 'item_photo',
  main: false,
  order: 0,
  photo_image_id: '',
  links: { photo_url: '' },
  _destroy: false,
  photo_url: '',
}

export const usePhotoManagerState = create<PhotoManagerState>((set, get) => ({
  photo: deepClone(emptyPhoto),
  photoAttributes: {},

  initializeFromItem: (photoObject: ItemPhotoForManager) => {
    const photo = deepClone(emptyPhoto)
    const existingPhoto = photoObject

    if (existingPhoto) {
      photo.id = Number(existingPhoto.id)
      photo.links = { photo_url: existingPhoto.photo_url }

      const photoPayload: CoreItemPhotoPayload = {
        id: photo.id,
        _destroy: false,
        main: true,
        order: 0,
      }

      set({
        photo,
        photoAttributes: photoPayload,
      })
    } else {
      set({
        photo: deepClone(emptyPhoto),
        photoAttributes: {},
      })
    }
  },

  deletePhoto: () => {
    const currentPhoto = get().photo
    const photoAttributes = get().photoAttributes

    if (currentPhoto) {
      currentPhoto._destroy = true
      currentPhoto.links.photo_url = ''
    }

    if (photoAttributes) {
      photoAttributes._destroy = true
      photoAttributes.photo_image_id = ''
    }

    set({
      photo: currentPhoto,
      photoAttributes: photoAttributes?._destroy ? {} : photoAttributes,
    })
  },

  uploadPhoto: async (file: File) => {
    try {
      useItemGeneratorAI.getState().setIsLoadingImageSingle(0, true)
      const base64 = await fileToBase64(file)
      const { id, urlTemporary } = await uploadImageByBase64(base64)

      const photo = get().photo || deepClone(emptyPhoto)
      let photoAttributes = get().photoAttributes

      if (id && urlTemporary) {
        photo.links.photo_url = urlTemporary

        photoAttributes = {
          ...photoAttributes,
          photo_image_id: id,
          order: 0,
          _destroy: false,
          main: true,
        }
      }

      set({
        photo,
        photoAttributes,
      })
    } finally {
      useItemGeneratorAI.getState().setIsLoadingImageSingle(0, false)
    }
  },

  generateSinglePhotoWithAI: async (title: string) => {
    try {
      useItemGeneratorAI.getState().setIsLoadingImageSingle(0, true)

      const { getImageWithAI } = useItemGeneratorAI.getState()
      const { id, urlTemporary } = await getImageWithAI(title, 0)

      if (id && urlTemporary) {
        const photo = get().photo || deepClone(emptyPhoto)
        let photoAttributes = get().photoAttributes

        photo.links.photo_url = urlTemporary

        photoAttributes = {
          photo_image_id: id,
          main: true,
          order: 0,
          _destroy: false,
        }

        set({
          photo,
          photoAttributes,
        })
      }
    } catch (e) {
      console.error('Error generating single image with AI.', e)
    } finally {
      useItemGeneratorAI.getState().setIsLoadingImageSingle(0, false)
    }
  },
}))
